'use server';

import { apiRequest } from '@/utils/api-request';

export interface Curriculum {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SchoolClass {
  id: number;
  curriculum_id: number;
  name: string;
  description?: string;
  created_at: string;
}

export interface Teacher {
  id: number;
  name: string;
  photo_url?: string | null;
}

export interface Subject {
  id: number;
  school_id_fk: number;
  name: string;
  code?: string | null;
  description?: string | null;
  category?: 'language' | 'core' | 'social' | 'activity' | 'skill' | 'sports' | 'elective' | null;
  created_at: string;
  updated_at: string;
}

export interface Board {
  id: number;
  name: string;
  code: string;
  description?: string;
}

export interface SubjectTemplate {
  id: number;
  subject_name: string;
  category: 'language' | 'core' | 'social' | 'activity' | 'skill' | 'sports' | 'elective';
  is_optional: boolean;
  stream: string | null;
  display_order: number;
}

export interface ClassSubjectMap {
  id: number;
  subject_id_fk: number;
  subject: Subject;
}

export interface SectionSubjectRow {
  class_subject_map_id: number;
  subject_id: number;
  subject: Subject;
  section_map_id: number | null;
  teacher_id: number | null;
  teacher: Teacher | null;
}

export interface ClassSection {
  id: number;
  class_id: number;
  section_label: string;
  primary_teacher_id?: number | null;
  secondary_teacher_id?: number | null;
  primaryTeacher?: Teacher | null;
  secondaryTeacher?: Teacher | null;
  created_at: string;
}

export interface CurriculumHierarchy {
  curriculum: Curriculum;
  classes: (SchoolClass & {
    PaSubjectMaps: ClassSubjectMap[];
    PaClassSections: ClassSection[];
  })[];
}

// Fetch full curriculum hierarchy
export async function getCurriculumHierarchy() {
  try {
    return await apiRequest('/api/parent-app/admin/curriculum/hierarchy', { method: 'GET' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch curriculum hierarchy');
  }
}

// Fetch all teachers for dropdown
export async function getTeachersForDropdown() {
  try {
    return await apiRequest('/api/parent-app/admin/teachers?limit=200', { method: 'GET' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch teachers');
  }
}

// ─── Curriculum CRUD ──────────────────────────────────────────────────────────

export async function getBoards(): Promise<Board[]> {
  try {
    const res = await apiRequest<Board[]>('/api/parent-app/admin/boards', { method: 'GET' });
    return (res.data ?? []) as Board[];
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch boards');
  }
}

export async function getCurriculumTemplates(boardId: number, grade: string, stream?: string): Promise<{ grade_label: string; templates: SubjectTemplate[] }> {
  try {
    const params = new URLSearchParams({ board_id: String(boardId), grade });
    if (stream) params.set('stream', stream);
    const res = await apiRequest<{ grade_label: string; templates: SubjectTemplate[] }>(`/api/parent-app/admin/curriculum-templates?${params}`, { method: 'GET' });
    return (res.data ?? { grade_label: grade, templates: [] }) as { grade_label: string; templates: SubjectTemplate[] };
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch curriculum templates');
  }
}

export async function applyTemplate(classId: number, payload: { stream?: string; subject_names: string[] }) {
  try {
    return await apiRequest(`/api/parent-app/admin/classes-new/${classId}/apply-template`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to apply template');
  }
}

export async function createCurriculum(payload: { name: string; board_id?: number | null }) {
  try {
    return await apiRequest('/api/parent-app/admin/curriculum', { method: 'POST', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create curriculum');
  }
}

export async function updateCurriculum(id: number, payload: { name: string; is_active?: boolean }) {
  try {
    return await apiRequest(`/api/parent-app/admin/curriculum/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update curriculum');
  }
}

export async function deleteCurriculum(id: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/curriculum/${id}`, { method: 'DELETE' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to delete curriculum');
  }
}

// ─── Class CRUD ───────────────────────────────────────────────────────────────

export async function createClass(payload: { curriculum_id: number; name: string; description?: string }) {
  try {
    return await apiRequest('/api/parent-app/admin/classes-new', { method: 'POST', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create class');
  }
}

export async function updateSchoolClass(id: number, payload: { name?: string; description?: string }) {
  try {
    return await apiRequest(`/api/parent-app/admin/classes-new/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update class');
  }
}

export async function deleteSchoolClass(id: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/classes-new/${id}`, { method: 'DELETE' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to delete class');
  }
}

// ─── Subject Master CRUD ──────────────────────────────────────────────────────

export async function getSubjectMaster() {
  try {
    return await apiRequest('/api/parent-app/admin/subjects', { method: 'GET' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch subjects');
  }
}

export async function createSubjectMaster(payload: { name: string; code?: string; description?: string; category?: string }) {
  try {
    return await apiRequest('/api/parent-app/admin/subjects', { method: 'POST', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create subject');
  }
}

export async function updateSubjectMaster(id: number, payload: { name?: string; code?: string; description?: string }) {
  try {
    return await apiRequest(`/api/parent-app/admin/subjects/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update subject');
  }
}

export async function deleteSubjectMaster(id: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/subjects/${id}`, { method: 'DELETE' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to delete subject');
  }
}

// ─── Class-Subject Mapping ────────────────────────────────────────────────────

export async function assignSubjectToClass(classId: number, subjectId: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/classes-new/${classId}/subjects`, {
      method: 'POST',
      body: JSON.stringify({ subject_id: subjectId })
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to assign subject to class');
  }
}

// ─── Per-Section Subject-Teacher Assignment ───────────────────────────────────

export async function getSectionSubjects(sectionId: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${sectionId}/subjects`, { method: 'GET' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch section subjects');
  }
}

export async function assignSectionSubjectTeacher(sectionId: number, subjectId: number, teacherId: number | null) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${sectionId}/subjects/${subjectId}/teacher`, {
      method: 'PUT',
      body: JSON.stringify({ teacher_id: teacherId })
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to assign teacher');
  }
}

export async function cloneSectionTeachers(targetSectionId: number, sourceSectionId: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${targetSectionId}/clone-from/${sourceSectionId}`, {
      method: 'POST'
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to clone section');
  }
}

export async function removeSubjectFromClass(classId: number, subjectId: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/classes-new/${classId}/subjects/${subjectId}`, { method: 'DELETE' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to remove subject from class');
  }
}

// ─── Section CRUD + Teacher Assignment ───────────────────────────────────────

export async function createSection(payload: { class_id: number; section_label: string; clone_from_section_id?: number }) {
  try {
    return await apiRequest('/api/parent-app/admin/sections', { method: 'POST', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create section');
  }
}

export async function updateSection(id: number, payload: { section_label: string }) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update section');
  }
}

export async function deleteSection(id: number) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${id}`, { method: 'DELETE' });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to delete section');
  }
}

export async function assignSectionTeachers(
  id: number,
  primaryTeacherId: number | null,
  secondaryTeacherId: number | null
) {
  try {
    return await apiRequest(`/api/parent-app/admin/sections/${id}/teachers`, {
      method: 'PUT',
      body: JSON.stringify({ primary_teacher_id: primaryTeacherId, secondary_teacher_id: secondaryTeacherId })
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to assign section teachers');
  }
}
